import React from 'react';
import { useCoffee } from '../context/CoffeeContext';
import { XIcon } from 'lucide-react';
import { FlavorProfile } from './FlavorProfile';
interface ComparisonModalProps {
  selectedEntries: string[];
  onClose: () => void;
}
export const ComparisonModal: React.FC<ComparisonModalProps> = ({
  selectedEntries,
  onClose
}) => {
  const {
    coffeeEntries,
    coffeeBags
  } = useCoffee();
  const selectedBrews = coffeeEntries.filter(entry => selectedEntries.includes(entry.id));
  const getBagName = (bagId: string) => {
    const bag = coffeeBags.find(b => b.id === bagId);
    return bag ? bag.name : 'Café Desconocido';
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-355">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-auto" data-id="element-356">
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center" data-id="element-357">
          <h3 className="text-lg font-bold text-amber-900" data-id="element-358">
            Comparación de Preparaciones
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-359">
            <XIcon size={24} data-id="element-360" />
          </button>
        </div>
        <div className="p-4" data-id="element-361">
          <div className="overflow-x-auto" data-id="element-362">
            <table className="w-full text-sm" data-id="element-363">
              <thead data-id="element-364">
                <tr className="bg-amber-50" data-id="element-365">
                  <th className="p-2 border text-left font-medium text-amber-800" data-id="element-366">
                    Parámetro
                  </th>
                  {selectedBrews.map(brew => <th key={brew.id} className="p-2 border text-left font-medium text-amber-800" data-id="element-367">
                      {getBagName(brew.bagId)}
                      <div className="text-xs font-normal text-amber-600" data-id="element-368">
                        {formatDate(brew.date)}
                      </div>
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-369">
                <tr data-id="element-370">
                  <td className="p-2 border font-medium" data-id="element-371">Método</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-372">
                      {brew.brewMethod}
                    </td>)}
                </tr>
                <tr data-id="element-373">
                  <td className="p-2 border font-medium" data-id="element-374">Café (g)</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-375">
                      {brew.coffeeWeight}
                    </td>)}
                </tr>
                <tr data-id="element-376">
                  <td className="p-2 border font-medium" data-id="element-377">Agua (ml)</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-378">
                      {brew.waterWeight}
                    </td>)}
                </tr>
                <tr data-id="element-379">
                  <td className="p-2 border font-medium" data-id="element-380">Ratio</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-381">
                      1:{(brew.waterWeight / brew.coffeeWeight).toFixed(1)}
                    </td>)}
                </tr>
                <tr data-id="element-382">
                  <td className="p-2 border font-medium" data-id="element-383">Molienda</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-384">
                      {brew.grindSize}
                    </td>)}
                </tr>
                <tr data-id="element-385">
                  <td className="p-2 border font-medium" data-id="element-386">Temperatura</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-387">
                      {brew.waterTemperature}°C
                    </td>)}
                </tr>
                <tr data-id="element-388">
                  <td className="p-2 border font-medium" data-id="element-389">Tiempo</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-390">
                      {formatTime(brew.totalBrewTime)}
                    </td>)}
                </tr>
                <tr data-id="element-391">
                  <td className="p-2 border font-medium" data-id="element-392">TDS (%)</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-393">
                      {brew.tds}
                    </td>)}
                </tr>
                <tr data-id="element-394">
                  <td className="p-2 border font-medium" data-id="element-395">Extracción (%)</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border" data-id="element-396">
                      {brew.extractionYield.toFixed(2)}
                    </td>)}
                </tr>
                <tr data-id="element-397">
                  <td className="p-2 border font-medium" data-id="element-398">Notas</td>
                  {selectedBrews.map(brew => <td key={brew.id} className="p-2 border whitespace-pre-line" data-id="element-399">
                      {brew.additionalNotes || '-'}
                    </td>)}
                </tr>
              </tbody>
            </table>
          </div>
          <h4 className="mt-6 mb-3 font-medium text-amber-800" data-id="element-400">
            Comparación de Perfiles de Sabor
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-401">
            {selectedBrews.map(brew => <div key={brew.id} className="bg-amber-50 p-3 rounded-lg" data-id="element-402">
                <h5 className="font-medium mb-2" data-id="element-403">{getBagName(brew.bagId)}</h5>
                <FlavorProfile profile={brew.flavorNotes} setProfile={() => {}} readOnly={true} data-id="element-404" />
              </div>)}
          </div>
        </div>
      </div>
    </div>;
};